import React, { useState, useEffect } from 'react';
import { X, FileText, Clock, MessageSquare, User } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { PublicInquiryForm } from './PublicInquiryForm';
import { InquiryPayload } from '../types';
import { supabase } from '../supabaseClient';

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedCity?: string;
  initialData?: InquiryPayload | null;
  onClearData?: () => void;
}

export const InquiryModal: React.FC<InquiryModalProps> = ({ isOpen, onClose, initialData, onClearData, preselectedCity }) => {
  const [view, setView] = useState<'COMMAND_CENTER' | 'FORM'>(initialData ? 'COMMAND_CENTER' : 'FORM');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!isOpen) return;

    // ⚡ SAFETY LOGIC: If there is data, show Command Center. If no data, show the Form.
    if (initialData) {
      setView('COMMAND_CENTER');
      const fetchAllData = async () => {
        setLoading(true);
        const {data: identity}=await supabase.rpc('crew_identity');
        if(identity?.role !== 'Admin') { setView('FORM'); setLoading(false); return; }
        const d = initialData as any;
        const companyName = (d?.companyName || d?.company || "").trim();
        const email = (d?.email || d?.client_email || "").trim();

        const { data: quoteData } = await supabase
          .from('quotes')
          .select('*')
          .ilike('client_company', `%${companyName}%`)
          .order('created_at', { ascending: false });

        const { data: leadData } = await supabase
          .from('event_leads') 
          .select('*')
          .or(`company_name.ilike.%${companyName}%,email.eq.${email}`)
          .order('created_at', { ascending: false });
        
        setQuotes(quoteData || []);
        setInquiries(leadData || []);
        setLoading(false);
      };

      fetchAllData();
    } else {
      setView('FORM');
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  // ⚡ VIEW 1: THE PUBLIC WEBSITE FORM (Restored!)
  if (view === 'FORM') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" onClick={onClose}></div>
        <div className="relative w-full max-w-4xl z-10 animate-fade-in-up">
           <GlassCard className="relative bg-slate-900 border-white/10">
              <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white z-50 bg-slate-900/50 rounded-full p-2"><X size={24} /></button>
              {/* This renders your actual online form component */}
              {/* Just remove the preselectedCity prop: */}
{/* Tell the form to close the modal when it finishes successfully: */}
<PublicInquiryForm onSuccess={onClose} />
           </GlassCard>
        </div>
      </div>
    );
  }

  // ⚡ VIEW 2: THE MEGA PIX COMMAND CENTER
  const d = (initialData || {}) as any;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" onClick={onClose}></div>

      <div className="relative w-full max-w-6xl z-10 animate-fade-in-up">
        <GlassCard className="w-full p-0 flex flex-col border-brand-neonBlue/30 shadow-2xl relative overflow-hidden bg-slate-900 min-h-[600px] max-h-[90vh]">
          
          <div className="flex items-center justify-between px-8 py-6 border-b border-white/10 bg-slate-950/50">
            <div className="flex items-center gap-4">
               <div className="w-12 h-12 bg-brand-neonBlue/10 rounded-2xl flex items-center justify-center text-brand-neonBlue">
                  <User size={24} />
               </div>
               <div>
                  <h2 className="text-2xl font-display font-bold text-white leading-tight">
                    {d?.companyName || d?.company_name || d?.company || 'Private Account'}
                  </h2>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">🚀 MEGA PIX COMMAND CENTER 🚀</p>
               </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button onClick={onClose} className="text-slate-400 hover:text-white p-2 transition-colors"><X size={28} /></button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
             <div className="w-full md:w-1/2 border-r border-white/5 p-8 overflow-y-auto custom-scrollbar bg-white/[0.01]">
                <h3 className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mb-6">
                  <MessageSquare size={14} className="text-brand-neonPink" /> Inquiry Feed
                </h3>
                
                <div className="space-y-4">
                  {inquiries.map((iq: any) => (
                    <div key={iq.id} className="p-5 rounded-2xl bg-white/5 border border-white/5 group hover:border-brand-neonPink/30 transition-all">
                       <div className="flex justify-between items-start mb-2">
                          <p className="text-white font-bold">{iq.event_type || iq.project_ref || 'General Inquiry'}</p>
                          <span className="text-[9px] bg-brand-neonPink/10 text-brand-neonPink px-2 py-0.5 rounded font-black uppercase tracking-tighter">Lead</span>
                       </div>
                       <p className="text-xs text-slate-400 line-clamp-2 italic mb-3">"{iq.additional_details || 'No notes provided.'}"</p>
                       <div className="flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                          <span className="flex items-center gap-1"><Clock size={10}/> {new Date(iq.created_at).toLocaleDateString()}</span>
                          <span className="text-slate-600">{iq.venue_location}</span>
                       </div>
                    </div>
                  ))}
                  {inquiries.length === 0 && <p className="text-center py-10 text-slate-600 text-sm italic">No original inquiry records found.</p>}
                </div>
             </div>

             <div className="w-full md:w-1/2 p-8 overflow-y-auto custom-scrollbar">
                <h3 className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mb-6">
                  <FileText size={14} className="text-brand-neonBlue" /> Commercial Quotes
                </h3>

                <div className="space-y-4">
                   {quotes.map((quote: any) => (
                     <div key={quote.id} className={`p-5 rounded-2xl border transition-all flex items-center justify-between ${quote.status === 'APPROVED' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/5 border-white/5 hover:border-brand-neonBlue/30'}`}>
                        <div>
                           <p className="text-white font-bold">{quote.title}</p>
                           <div className="flex items-center gap-3 mt-1">
                              <span className="text-[10px] text-slate-500 font-bold">{new Date(quote.created_at).toLocaleDateString()}</span>
                              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${quote.status === 'APPROVED' ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300'}`}>
                                {quote.status}
                              </span>
                           </div>
                        </div>
                        <div className="text-right">
                           <p className="text-lg font-mono font-bold text-white">${(Number(quote.total || quote.grand_total || quote.grandTotal) || 0).toLocaleString()}</p>
                        </div>
                     </div>
                   ))}
                   {quotes.length === 0 && <p className="text-center py-10 text-slate-600 text-sm italic">No quotes have been generated yet.</p>}
                </div>
             </div>

          </div>
        </GlassCard>
      </div>
    </div>
  );
};
